import { useEffect, useImperativeHandle, useRef, type ReactNode, type Ref } from 'react';
import { cryptoRandomInt, teamOfSeat, type Seat } from '../game/match';
import { avatarUrl } from '../lib/api';
import { tick } from '../lib/sound';
import type { SeatPlayer, Teams } from '../shared/schemas';
import { avatarColor, initials } from './ui';

export interface WheelHandle {
  /** Animates to `seat` (already chosen fairly by the caller). Resolves when it stops. */
  spinTo(seat: Seat): Promise<void>;
  /** Jumps to `seat` without animation (e.g. on reload). */
  showSeat(seat: Seat): void;
}

interface Props {
  seats: SeatPlayer[];
  teams: Teams;
  ref?: Ref<WheelHandle>;
  /** Laid over the hub, e.g. the SPIN button. */
  center?: ReactNode;
}

const R = 96;
const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Point on a circle; angles are degrees clockwise from 12 o'clock. */
function polar(r: number, deg: number): [number, number] {
  const rad = (deg * Math.PI) / 180;
  return [r * Math.sin(rad), -r * Math.cos(rad)];
}

function segmentPath(i: number): string {
  const [x0, y0] = polar(R, i * 90);
  const [x1, y1] = polar(R, (i + 1) * 90);
  return `M0 0 L${x0} ${y0} A${R} ${R} 0 0 1 ${x1} ${y1} Z`;
}

/** Wheel rotation (deg) that puts the middle of segment `seat` under the top pointer. */
const restAngle = (seat: Seat) => -(seat * 90 + 45);

export function Wheel({ seats, teams, ref, center }: Props) {
  const rotor = useRef<SVGGElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const angle = useRef(restAngle(0) + 0);
  const frame = useRef(0);

  const apply = (deg: number) => {
    angle.current = deg;
    rotor.current?.setAttribute('transform', `rotate(${deg})`);
  };

  useEffect(() => () => cancelAnimationFrame(frame.current), []);

  useImperativeHandle(ref, () => ({
    showSeat(seat) {
      apply(restAngle(seat));
    },
    spinTo(seat) {
      cancelAnimationFrame(frame.current);
      // Land somewhere inside the segment (not dead centre) so it feels natural.
      const jitter = cryptoRandomInt(51) - 25;
      const target = restAngle(seat) + jitter;
      const from = angle.current;
      const toMod = (((target - from) % 360) + 360) % 360;

      if (reducedMotion()) {
        const el = svg.current;
        return new Promise<void>((resolve) => {
          el?.animate([{ opacity: 1 }, { opacity: 0.15 }, { opacity: 1 }], { duration: 450, easing: 'ease-in-out' });
          setTimeout(() => apply(from + toMod), 225);
          setTimeout(resolve, 450);
        });
      }

      const turns = 5 + cryptoRandomInt(3); // 5–7 full rotations
      const delta = turns * 360 + toMod;
      const duration = 4000 + cryptoRandomInt(2001); // 4–6 s
      const start = performance.now();
      let lastSegment = Math.floor(from / 90);
      return new Promise<void>((resolve) => {
        const step = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const deg = from + delta * easeOutCubic(t);
          apply(deg);
          const segment = Math.floor(deg / 90);
          if (segment !== lastSegment) {
            lastSegment = segment;
            tick();
          }
          if (t < 1) frame.current = requestAnimationFrame(step);
          else resolve();
        };
        frame.current = requestAnimationFrame(step);
      });
    },
  }));

  return (
    <div className="relative mx-auto aspect-square w-full max-w-[22rem]">
      {center && <div className="absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2">{center}</div>}
      {/* Pointer at the top */}
      <svg viewBox="-12 0 24 22" className="absolute top-[-6px] left-1/2 z-10 w-8 -translate-x-1/2 drop-shadow-[0_3px_2px_rgb(0_0_0/0.5)]" aria-hidden>
        <path d="M-11 0 H11 L0 21 Z" fill="var(--color-gold)" stroke="#5b3a1e" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
      <svg ref={svg} viewBox="-104 -104 208 208" className="h-full w-full drop-shadow-[0_12px_20px_rgb(0_0_0/0.45)]" aria-hidden>
        <defs>
          <clipPath id="wheel-avatar">
            <circle r="19" />
          </clipPath>
        </defs>
        <circle r="102" fill="#5b3a1e" />
        <circle r="99" fill="none" stroke="var(--color-gold)" strokeWidth="2" />
        <g ref={rotor} transform={`rotate(${angle.current})`}>
          {([0, 1, 2, 3] as Seat[]).map((seat) => {
            const p = seats[seat]!;
            const color = teams[teamOfSeat(seat)].color;
            const mid = seat * 90 + 45;
            return (
              <g key={seat}>
                <path d={segmentPath(seat)} fill={color} stroke="var(--color-cream)" strokeWidth="1.5" />
                {/* Shade alternate halves so neighbours of the same colour stay distinct. */}
                <path d={segmentPath(seat)} fill={seat < 2 ? 'rgb(255 255 255 / 0.08)' : 'rgb(0 0 0 / 0.1)'} />
                <g transform={`rotate(${mid})`}>
                  <g transform="translate(0 -52)">
                    <circle r="21.5" fill="var(--color-cream)" />
                    {p.avatarKey ? (
                      <image
                        href={avatarUrl(p.avatarKey)}
                        x="-19"
                        y="-19"
                        width="38"
                        height="38"
                        clipPath="url(#wheel-avatar)"
                        preserveAspectRatio="xMidYMid slice"
                      />
                    ) : (
                      <>
                        <circle r="19" fill={avatarColor(p.name)} />
                        <text textAnchor="middle" dominantBaseline="central" fill="#fff" fontSize="14" fontWeight="700">
                          {initials(p.name)}
                        </text>
                      </>
                    )}
                  </g>
                  <text
                    y="-84"
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#fff"
                    fontSize="9.5"
                    fontWeight="700"
                    direction="ltr"
                    style={{ unicodeBidi: 'plaintext' }}
                    stroke="rgb(0 0 0 / 0.35)"
                    strokeWidth="2.5"
                    paintOrder="stroke"
                  >
                    {[...p.name].length > 9 ? `${[...p.name].slice(0, 8).join('')}…` : p.name}
                  </text>
                </g>
              </g>
            );
          })}
        </g>
        <circle r="17" fill="var(--color-cream)" stroke="var(--color-gold)" strokeWidth="2" />
        <text textAnchor="middle" dominantBaseline="central" fontSize="17" fill="var(--color-ink)">
          ♠
        </text>
      </svg>
    </div>
  );
}

/** The big round SPIN button that sits in the middle of the wheel. */
export function SpinHub({ onSpin, disabled }: { onSpin: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onSpin();
      }}
      disabled={disabled}
      aria-label="Spin the wheel"
      className="grid h-[4.6rem] w-[4.6rem] place-items-center rounded-full font-display text-lg font-extrabold tracking-wider text-ink uppercase shadow-[0_6px_0_0_#9c7a2f,0_14px_26px_-8px_rgb(0_0_0/0.7)] ring-4 ring-cream transition hover:brightness-105 active:translate-y-1 active:shadow-[0_2px_0_0_#9c7a2f,0_8px_16px_-8px_rgb(0_0_0/0.7)] disabled:opacity-60"
      style={{ background: 'radial-gradient(circle at 35% 30%, #fde68a, #d4a84b 60%, #b9812a)' }}
    >
      <span className="leading-none">
        Spin
        <span className="mt-0.5 block text-[10px] font-bold tracking-[0.2em] text-ink/60">tap</span>
      </span>
    </button>
  );
}
